<template>
  <!-- En édition, les cellules (hors lignes de regroupement .full-row) n'ont pas de padding vertical -->
  <div :class="editable ? '[&_td:not(.full-row)]:py-0!' : ''">
    <Table :columns="columns" :data="rows" v-if="rows.length > 0" class="mt-2">
      <template #editableSeller="{ row, column }">
        <div class="flex flex-row-reverse">
          <FormField
            style="max-width: 6rem"
            v-if="retributionId(row)"
            :id="`seller-retribution-${retributionId(row)}`"
            :name="column.field"
            v-model="row[column.field]"
            :fieldClass="`${$options.components.FormField.props.fieldClass.default} text-right`"
            @input="$emit('edit-seller-retribution', retributionId(row), row[column.field])"
          />
        </div>
      </template>
      <template #editableManager="{ row, column }">
        <div class="flex flex-row-reverse">
          <FormField
            style="max-width: 6rem"
            v-if="retributionId(row)"
            :id="`manager-retribution-${retributionId(row)}`"
            :name="column.field"
            v-model="row[column.field]"
            :fieldClass="`${$options.components.FormField.props.fieldClass.default} text-right`"
            @input="$emit('edit-manager-retribution', retributionId(row), row[column.field])"
          />
        </div>
      </template>
      <template #SellerRetribution="{ row, column }">
        <div v-if="$slots.retribution">
          <slot name="seller-retribution" :retributionId="retributionId(row)" />
        </div>
        <span v-else>{{ row[column.field] ? formatPrice(row[column.field]) : "—" }}</span>
      </template>
      <template #ManagerRetribution="{ row, column }">
        <div v-if="$slots.retribution">
          <slot name="manager-retribution" :retributionId="retributionId(row)" />
        </div>
        <span v-else>{{ row[column.field] ? formatPrice(row[column.field]) : "—" }}</span>
      </template>
      <template #categoryName="{ row, colspan }">
        <td class="full-row font-bold bg-purple-200 px-4 py-2 text-xl" :colspan="colspan">
          {{ row.categoryName }}
        </td>
      </template>
      <template #duration="{ row, colspan }">
        <td class="full-row bg-purple-100 px-4 py-2 font-semibold" :colspan="colspan">{{ row.duration }}</td>
      </template>
    </Table>
    <Alert v-else>{{ $t("no_data") }}</Alert>
  </div>
</template>

<script>
import { Alert, Table, FormField, formatPrice, formatPriceBand } from "@estaly/ui";

export default {
  emits: ["edit-seller-retribution", "edit-manager-retribution"],

  components: { Alert, Table, FormField },

  props: {
    retributions: {
      type: Array,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    hideStoreRemuneration: {
      type: Boolean,
      default: false,
    },
    hideSellerRetribution: {
      type: Boolean,
      default: false,
    },
    hideManagerRetribution: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      defaultColumns: [
        { field: "priceBand", label: "Prix du produit" },
        { field: "price", label: "Prix du contrat", numeric: true },
        { field: "remuneration", label: "Rémunération", numeric: true },
        {
          field: "sellerRetribution",
          label: "Prime vendeur",
          numeric: true,
          slot: this.editable ? "editableSeller" : "SellerRetribution",
        },
        {
          field: "managerRetribution",
          label: "Prime manager",
          numeric: true,
          slot: this.editable ? "editableManager" : "ManagerRetribution",
        },
      ],
    };
  },

  computed: {
    columns() {
      return this.defaultColumns.filter((column) => {
        if (this.hideStoreRemuneration && column.field === "remuneration") return false;
        if (this.hideSellerRetribution && column.field === "sellerRetribution") return false;
        if (this.hideManagerRetribution && column.field === "managerRetribution") return false;
        return true;
      });
    },

    rows() {
      return this.categories.map((category) => this.categoryRows(category)).flat();
    },

    categories() {
      return [...new Set(this.retributions.map((row) => row.category))];
    },

    months() {
      const months = this.retributions.filter((row) => !row.monthlyBilling);
      return [...new Set(months.map((row) => row.termLength))].sort((a, b) => a - b);
    },
  },

  methods: {
    formatPrice,

    retributionId(row) {
      return row["id"];
    },

    categoryRows(category) {
      const data = this.retributions.filter((retribution) => retribution.category === category);
      const rows = [{ fullRow: true, slot: "categoryName", categoryName: data[0].categoryName }];

      const monthlyRows = data
        .filter((retribution) => retribution.monthlyBilling)
        .map(this.row)
        .sort(this.sortRows);
      if (monthlyRows.length > 0) {
        rows.push({ fullRow: true, slot: "duration", duration: this.$t("termLength.monthly") });
        rows.push(...monthlyRows);
      }

      this.months.forEach((termLength) => {
        const yearlyRows = data
          .filter((retribution) => !retribution.monthlyBilling && retribution.termLength === termLength)
          .map(this.row)
          .sort(this.sortRows);
        if (yearlyRows.length > 0) {
          rows.push({ fullRow: true, slot: "duration", duration: this.$t(`termLength.${termLength}_months`) });
          rows.push(...yearlyRows);
        }
      });
      return rows;
    },

    row(retribution) {
      return {
        id: retribution.id,
        priceBandMin: retribution.priceBandMin,
        termLength: retribution.termLength,
        monthlyBilling: retribution.monthlyBilling,
        category: retribution.category,
        priceBand: formatPriceBand(retribution),
        price: formatPrice(retribution.price),
        remuneration: formatPrice(retribution.storeCommission),
        sellerRetribution: retribution.sellerNetAmount,
        managerRetribution: retribution.managerNetAmount,
      };
    },

    formatDuration(retribution) {
      if (retribution.monthlyBilling) return this.$t("termLength.monthly");
      return this.$t(`termLength.${retribution.termLength}_months`);
    },

    sortRows(a, b) {
      if (a.termLength === b.termLength) return a.priceBandMin - b.priceBandMin;
      return a.termLength - b.termLength;
    },
  },
};
</script>
